// src/components/YaklasanTaksitler.js
//
// Planlanmış tahsilatlar (`scheduledPayments`) dashboard'da HİÇ toplu görünmüyordu;
// yalnızca tek tek müşterinin Cari Hesap Ekstresi sayfasından erişilebiliyordu.
// Bu kart, önümüzdeki 7 gün içinde vadesi dolan 'Bekleniyor' taksitleri listeler.
//
// Not: "Teslimi Yaklaşanlar" kartıyla karıştırılmamalı — o kart iş emri TESLİM
// tarihine bakar, buradaki ise gerçek VADE tarihidir (`vadeTarihi`).

import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, onSnapshot, orderBy, limit, Timestamp } from 'firebase/firestore';
import { Paper, Typography, Box, Stack, Divider, CircularProgress, Link as MuiLink, Chip } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { format, addDays, startOfDay, isBefore } from 'date-fns';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';

// Sorgu tavanı: dashboard'da hiçbir dinleyici sınırsız okumaz.
const SORGU_LIMITI = 60;
const LISTE_LIMITI = 6;

const formatCurrency = (amount, finansGizli) => {
    if (finansGizli) return '**** ₺';
    return (amount || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' });
};

const TaksitSatiri = ({ taksit, finansGizli }) => {
    const vade = taksit.vadeTarihi?.toDate ? taksit.vadeTarihi.toDate() : null;
    const gecti = vade ? isBefore(vade, startOfDay(new Date())) : false;

    return (
        <MuiLink component={RouterLink} to={`/cari-hesaplar/${taksit.musteriId}`} sx={{ textDecoration: 'none' }}>
            <Paper
                variant="outlined"
                sx={{
                    p: 1.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    '&:hover': { backgroundColor: 'action.hover' },
                    backgroundColor: '#fff'
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight="bold" noWrap>{taksit.musteriAdSoyad || 'Müşteri'}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                        {taksit.odemeYontemi || 'Ödeme yöntemi belirtilmemiş'}
                    </Typography>
                </Box>
                <Box textAlign="right">
                    <Typography color={gecti ? 'error.main' : 'warning.main'} fontWeight="bold">
                        {formatCurrency(taksit.tutar, finansGizli)}
                    </Typography>
                    <Typography variant="caption" color={gecti ? 'error.main' : 'text.secondary'}>
                        {vade ? format(vade, 'dd/MM/yyyy') : 'Vade yok'}
                    </Typography>
                </Box>
            </Paper>
        </MuiLink>
    );
};

const YaklasanTaksitler = ({ finansGizli }) => {
    const [taksitler, setTaksitler] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const bugun = startOfDay(new Date());
        const yediGunSonra = addDays(bugun, 7);

        // Vadesi GEÇMİŞ ama hâlâ 'Bekleniyor' olan taksitler de görünmeli:
        // alt sınır bugün değil, 30 gün öncesi. Aksi halde kaçırılan bir vade
        // dashboard'dan tamamen kaybolurdu.
        const altSinir = addDays(bugun, -30);

        const q = query(
            collection(db, 'scheduledPayments'),
            where('durum', '==', 'Bekleniyor'),
            where('vadeTarihi', '>=', Timestamp.fromDate(altSinir)),
            where('vadeTarihi', '<=', Timestamp.fromDate(yediGunSonra)),
            orderBy('vadeTarihi', 'asc'),
            limit(SORGU_LIMITI)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setTaksitler(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
            setLoading(false);
        }, (error) => {
            console.error('Yaklaşan taksitler çekilemedi:', error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const gecikmisSayisi = taksitler.filter(t => {
        const vade = t.vadeTarihi?.toDate ? t.vadeTarihi.toDate() : null;
        return vade ? isBefore(vade, startOfDay(new Date())) : false;
    }).length;

    const basliktakiSayi = taksitler.length === SORGU_LIMITI ? `${SORGU_LIMITI}+` : taksitler.length;

    return (
        <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <EventRepeatIcon color="secondary" />
                <Typography variant="h6">Yaklaşan Taksitler ({basliktakiSayi})</Typography>
            </Box>
            {gecikmisSayisi > 0 && (
                <Chip size="small" color="error" variant="outlined" sx={{ alignSelf: 'flex-start', mb: 1 }}
                    label={`${gecikmisSayisi} taksitin vadesi geçti`} />
            )}
            <Divider />
            <Stack spacing={1.5} sx={{ mt: 2, flexGrow: 1 }}>
                {loading ? (
                    <CircularProgress size={24} sx={{ mx: 'auto' }} />
                ) : taksitler.length > 0 ? (
                    <>
                        {taksitler.slice(0, LISTE_LIMITI).map(t => (
                            <TaksitSatiri key={t.id} taksit={t} finansGizli={finansGizli} />
                        ))}
                        {taksitler.length > LISTE_LIMITI && (
                            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                                +{taksitler.length - LISTE_LIMITI} taksit daha
                            </Typography>
                        )}
                    </>
                ) : (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', pt: 2 }}>
                        Yaklaşan planlı tahsilat yok.
                    </Typography>
                )}
            </Stack>
        </Paper>
    );
};

export default YaklasanTaksitler;
